// Copyright (c) 2016 - 2017 Uber Technologies, Inc.
// Copyright (c) 2020 Catalyst IT
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import React from 'react';

const CLASSES = {
  bottom: 'rv-xy-plot__borders-bottom',
  container: 'rv-xy-plot__borders',
  left: 'rv-xy-plot__borders-left',
  right: 'rv-xy-plot__borders-right',
  top: 'rv-xy-plot__borders-top',
};

const STYLE = { fill: '#fff', strokeWidth: 0 };

interface Props {
  padding: number;
}
interface PropsFromReactVis {
  marginTop: number;
  marginBottom: number;
  marginLeft: number;
  marginRight: number;
  innerWidth: number;
  innerHeight: number;
}

/**
 * A fork of the standard React-Vis `Borders` component, which allows you to
 * specify some padding between the edge of the graph area and the masking
 * rectangles. This is done so that if a data line runs directly along the
 * edge of the plot, the full line will be rendered and visible.
 * @param props
 */
export function BordersPadded(props: Props) {
  const {
    padding,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    innerWidth,
    innerHeight,
  } = props as Props & PropsFromReactVis;
  const height = innerHeight + marginTop + marginBottom;
  const width = innerWidth + marginLeft + marginRight;
  return (
    <g className={CLASSES.container}>
      {marginBottom >= padding ? (
        <rect
          className={CLASSES.bottom}
          style={STYLE}
          x={0}
          y={height - (marginBottom - padding)}
          width={width}
          height={marginBottom - padding}
        />
      ) : null}
      {marginLeft >= padding ? (
        <rect
          className={CLASSES.left}
          style={STYLE}
          x={0}
          y={0}
          width={marginLeft - padding}
          height={height}
        />
      ) : null}
      {marginRight >= padding ? (
        <rect
          className={CLASSES.right}
          style={STYLE}
          x={width - (marginRight - padding)}
          y={0}
          width={marginRight - padding}
          height={height}
        />
      ) : null}
      {marginTop >= padding ? (
        <rect
          className={CLASSES.top}
          style={STYLE}
          x={0}
          y={0}
          width={width}
          height={marginTop - padding}
        />
      ) : null}
    </g>
  );
}

BordersPadded.displayName = 'BordersPadded';
BordersPadded.requiresSVG = true;
